import React, { useState, useEffect } from 'react';
import {
  Keyboard,
  RotateCcw,
  CheckCircle2,
  Zap,
  Target,
  Clock3,
  Sparkles,
  Trophy,
} from 'lucide-react';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import PageHeader from '../components/ui/PageHeader';

const GERMAN_TEXT_POOL = [
  'Deutsch zu lernen macht unglaublich viel Spaß und eröffnet viele Möglichkeiten im Berufsleben.',
  'Die Grammatik mag am Anfang kompliziert erscheinen, aber sie folgt sehr klaren Regeln.',
  'Können Sie mir bitte den Weg zum nächsten Bahnhof zeigen? Ich muss den Zug nach Berlin nehmen.',
  'In Deutschland gibt es viele verschiedene Dialekte, aber Hochdeutsch wird überall verstanden.',
  'Das Wetter heute ist wunderschön, die Sonne scheint und es gibt kaum Wolken am blauen Himmel.',
  'Viele Studenten aus der ganzen Welt kommen nach Berlin, um an den exzellenten Universitäten zu studieren.',
  'Gestern Abend habe ich mit meinen Freunden in einem gemütlichen Restaurant in der Innenstadt gegessen.',
  'Man sagt, dass Übung den Meister macht, und das gilt besonders für das Erlernen einer neuen Sprache.',
  'Die Alpen im Süden Deutschlands bieten atemberaubende Landschaften zum Wandern und Skifahren an.',
  'Ich freue mich darauf, im nächsten Sommer eine lange Reise durch ganz Europa zu machen.',
  'Es ist wichtig, jeden Tag ein wenig zu üben, um flüssiger sprechen und schreiben zu können.',
  'Technik und Innovation sind zwei Bereiche, in denen Deutschland weltweit eine führende Rolle spielt.',
  'Haben Sie schon einmal eine echte deutsche Currywurst probiert? Sie ist sehr typisch für die Hauptstadt.',
  'Die Komplexität der deutschen Sprache erfordert viel Geduld, aber die Mühe lohnt sich auf jeden Fall.',
];

const TypingPracticePage = () => {
  const [targetText, setTargetText] = useState(GERMAN_TEXT_POOL.join(' '));
  const [userInput, setUserInput] = useState('');
  const [isStarted, setIsStarted] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [stats, setStats] = useState({ wpm: 0, accuracy: 100, errors: 0 });

  const resetPractice = () => {
    setUserInput('');
    setIsStarted(false);
    setStartTime(null);
    setIsCompleted(false);
    setStats({ wpm: 0, accuracy: 100, errors: 0 });
    setTargetText([...GERMAN_TEXT_POOL].sort(() => Math.random() - 0.5).join(' '));
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isCompleted) return;
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      if (!isStarted && e.key.length === 1) {
        setIsStarted(true);
        setStartTime(Date.now());
      }

      if (e.key === 'Backspace') {
        setUserInput((prev) => prev.slice(0, -1));
        return;
      }

      if (e.key.length !== 1) return;

      setUserInput((prev) => {
        if (prev.length >= targetText.length) return prev;
        const next = prev + e.key;
        if (next === targetText) setIsCompleted(true);
        return next;
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isStarted, isCompleted, targetText]);

  useEffect(() => {
    if (userInput.length === 0) return;

    let errorCount = 0;
    for (let i = 0; i < userInput.length; i++) {
      if (userInput[i] !== targetText[i]) errorCount++;
    }

    const accuracy = Math.max(0, Math.round(((userInput.length - errorCount) / userInput.length) * 100));

    let wpm = 0;
    if (startTime) {
      const timeElapsedMinutes = (Date.now() - startTime) / 60000;
      const wordsTyped = userInput.length / 5;
      wpm = Math.round(wordsTyped / (timeElapsedMinutes || 1));
    }

    setStats({ wpm, accuracy, errors: errorCount });
  }, [userInput, startTime, targetText]);

  const renderText = () => {
    return targetText.split('').map((char, index) => {
      let color = 'text-zinc-300';
      if (index < userInput.length) {
        color = userInput[index] === char ? 'text-zinc-900 border-b-2 border-emerald-500' : 'text-white bg-[#DD0000] rounded-xs';
      } else if (index === userInput.length) {
        color = 'text-zinc-950 border-b-2 border-[#DD0000] bg-amber-50 animate-pulse';
      }

      return (
        <span key={index} className={`font-mono text-xl sm:text-2xl transition-all duration-75 ${color}`}>
          {char}
        </span>
      );
    });
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-1 pb-10 font-sans sm:px-3">
        {/* Unified Header */}
        <PageHeader
          icon={Keyboard}
          badge="Shpejtësi & Saktësi"
          title="Shtypi në Gjermanisht"
          description="Përmirëso shpejtësinë e të shkruarit në gjermanisht, mëso vendosjen e shkronjave me karaktere speciale (ä, ö, ü, ß)."
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={resetPractice}
              className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-3.5 py-2 text-xs font-bold text-zinc-700 hover:bg-zinc-50 transition shadow-2xs"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Rifillo Tekstin
            </button>
          </div>
        </PageHeader>

        {/* Live HUD Stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-zinc-200/80 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-extrabold uppercase tracking-wider text-zinc-400">Shpejtësia (WPM)</span>
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
                <Zap size={18} />
              </div>
            </div>
            <div className="mt-3 text-3xl font-black text-zinc-900">{stats.wpm}</div>
            <p className="mt-1 text-xs text-zinc-500">Fjalë për minutë</p>
          </div>

          <div className="rounded-2xl border border-zinc-200/80 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-extrabold uppercase tracking-wider text-zinc-400">Saktësia</span>
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                <Target size={18} />
              </div>
            </div>
            <div className="mt-3 text-3xl font-black text-zinc-900">{stats.accuracy}%</div>
            <p className="mt-1 text-xs text-zinc-500">
              {stats.errors === 0 ? 'Pa asnjë gabim' : `${stats.errors} gabime të bëra`}
            </p>
          </div>

          <div className="rounded-2xl border border-zinc-200/80 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-extrabold uppercase tracking-wider text-zinc-400">Koha e Lëvizjes</span>
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-zinc-100 text-zinc-700">
                <Clock3 size={18} />
              </div>
            </div>
            <div className="mt-3 text-3xl font-black text-zinc-900">
              {startTime ? `${Math.round((Date.now() - startTime) / 1000)}s` : '0s'}
            </div>
            <p className="mt-1 text-xs text-zinc-500">Kohë aktive</p>
          </div>
        </div>

        {/* Main Typing Card */}
        <div className="relative overflow-hidden rounded-2xl border border-zinc-200/80 bg-white p-6 sm:p-10 shadow-sm min-h-[300px] flex flex-col justify-center">
          <div aria-hidden="true" className="absolute top-0 inset-x-0 flex h-1">
            <span className="flex-1 bg-zinc-950" />
            <span className="flex-1 bg-[#DD0000]" />
            <span className="flex-1 bg-[#FFCE00]" />
          </div>

          {!isStarted && !isCompleted && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[2px]">
              <div className="rounded-xl border border-zinc-200 bg-white px-5 py-3 text-xs font-extrabold text-zinc-900 shadow-md animate-pulse">
                Filloni të shtypni me tastierë...
              </div>
            </div>
          )}

          <div className="relative z-0 leading-relaxed tracking-normal select-none">
            {renderText()}
          </div>

          {isCompleted && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/95 p-6 text-center animate-in fade-in zoom-in-95 duration-300">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 shadow-sm mb-4">
                <CheckCircle2 size={32} />
              </div>
              <h2 className="text-2xl font-extrabold text-zinc-900">Sfidë e Përfunduar me Sukses!</h2>
              <p className="mt-1 text-sm text-zinc-500 max-w-md">
                Keni shkruar të gjithë tekstin me një shpejtësi prej <strong className="text-zinc-900">{stats.wpm} WPM</strong> dhe saktësi prej <strong className="text-emerald-600">{stats.accuracy}%</strong>.
              </p>
              <button
                type="button"
                onClick={resetPractice}
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-zinc-950 px-6 py-3 text-xs font-extrabold text-white hover:bg-[#DD0000] transition"
              >
                Provo Tekst Tjetër <RotateCcw size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TypingPracticePage;
